import {
    type RouteTypeAttributesType,
    RouteTypeRequestSchema,
    type RouteTypeRequestType,
    type RouteTypeResponseType,
} from './RouteTypeApi';
import { extractFieldsFromB } from '../../utilities/SchemaManager';

export class RouteType {
    private _innerId: string;
    private _response: RouteTypeResponseType | null = null;

    constructor() {
        this._innerId = Math.random().toString(36).substring(7);
    }

    set(response: RouteTypeResponseType) {
        this._response = response;
    }

    get(): RouteTypeResponseType | null {
        return this._response;
    }

    getId(): number {
        return this._response?.id || -1;
    }

    getName(): string {
        return this._response?.name || '';
    }

    setRouteCalcType(calcType: string): this {
        if (this._response) {
            this._response.attributes.type = calcType;
        }
        return this;
    }

    getRouteCalcType(): string {
        return this._response?.attributes.type || '';
    }

    public getRequest(): RouteTypeRequestType {
        if (!this._response) {
            throw new Error('RouteType.getRequest: response is not set');
        }
        return extractFieldsFromB<RouteTypeRequestType>(this._response, RouteTypeRequestSchema);
    }

    getAttributes(): RouteTypeAttributesType {
        return (
            this._response?.attributes || {
                type: 'optimal',
                vehicle: {
                    weight: 0,
                    load: 0,
                    width: 0,
                    length: 0,
                    height: 0,
                    category: [],
                    euro_norm: [],
                },
                start_time: '',
            }
        );
    }

    isActive(): boolean {
        return this._response?.active || false;
    }

    setActive(active: boolean): this {
        if (this._response) {
            this._response.active = active;
        }
        return this;
    }

    setAttributes(attributes: RouteTypeAttributesType): this {
        if (this._response) {
            this._response.attributes = attributes;
        }
        return this;
    }

    setName(name: string): this {
        if (this._response) {
            this._response.name = name;
        }
        return this;
    }

    setVehicleLength(length: number): this {
        if (this._response) {
            if (length < 0) length = 0;
            this._response.attributes.vehicle.length = length;
        }
        return this;
    }

    setVehicleWidth(width: number): this {
        if (this._response) {
            if (width < 0) width = 0;
            if (width > 10) width = 10;
            this._response.attributes.vehicle.width = width;
        }
        return this;
    }

    setVehicleWeight(weight: number): this {
        if (this._response) {
            if (weight < 0) weight = 0;
            if (weight > 40) weight = 40;
            this._response.attributes.vehicle.weight = weight;
        }
        return this;
    }

    setVehicleHeight(height: number): this {
        if (this._response) {
            if (height < 0) height = 0;
            if (height > 5) height = 5;
            this._response.attributes.vehicle.height = height;
        }
        return this;
    }

    setVehicleLoad(load: number): this {
        if (this._response) {
            if (load < 0) load = 0;
            if (load > 40) load = 40;
            this._response.attributes.vehicle.load = load;
        }
        return this;
    }

    setStartTime(hour: string): this {
        if (this._response) {
            if (!hour || hour.match(/^(\d{1,2}:\d{2})?$/)) {
                this._response.attributes.start_time = hour || '';
            }
        }
        return this;
    }

    setEuroNorm(euro_norm: number): this {
        if (this._response) {
            this._response.attributes.vehicle.euro_norm = [euro_norm];
        }
        return this;
    }
}
